import { useEffect, useRef } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';
import { cn } from '../../utils/cn';
import { useNotificationStore } from '../../store/notificationStore';
import type { ToastData } from '../../types';

const iconMap = {
  success: CheckCircle,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
} as const;

const colorMap = {
  success: {
    bg: 'bg-green-50 border-green-200',
    icon: 'text-green-500',
    text: 'text-green-800',
  },
  error: {
    bg: 'bg-red-50 border-red-200',
    icon: 'text-red-500',
    text: 'text-red-800',
  },
  warning: {
    bg: 'bg-amber-50 border-amber-200',
    icon: 'text-amber-500',
    text: 'text-amber-800',
  },
  info: {
    bg: 'bg-blue-50 border-blue-200',
    icon: 'text-blue-500',
    text: 'text-blue-800',
  },
} as const;

export function Toast({ id, type, message, autoDismiss, duration }: ToastData) {
  const removeToast = useNotificationStore((s) => s.removeToast);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (autoDismiss && duration > 0 && isFinite(duration)) {
      timerRef.current = setTimeout(() => {
        removeToast(id);
      }, duration);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [autoDismiss, duration, id, removeToast]);

  const Icon = iconMap[type];
  const colors = colorMap[type];

  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-lg border p-4 shadow-md',
        'animate-toast-slide-in motion-reduce:animate-none',
        colors.bg
      )}
    >
      <Icon className={cn('h-5 w-5 shrink-0 mt-0.5', colors.icon)} aria-hidden="true" />
      <p className={cn('text-sm flex-1', colors.text)}>{message}</p>
      <button
        onClick={() => removeToast(id)}
        className={cn(
          'shrink-0 rounded-md p-1 transition-colors',
          'hover:bg-black/10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-current',
          colors.text
        )}
        aria-label="Dismiss notification"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}
